import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import adminService from '../../services/adminService';

// Async thunks
export const fetchUsers = createAsyncThunk(
  'admin/fetchUsers',
  async (params, { rejectWithValue }) => {
    try {
      console.log('🔄 fetchUsers thunk - Starting with params:', params);
      const response = await adminService.getUsers(params);
      console.log('✅ fetchUsers thunk - Success:', response);
      return response; // Returns {success, data: {users, pagination}}
    } catch (error) {
      console.error('❌ fetchUsers thunk - Error:', error);
      return rejectWithValue(error.message);
    }
  }
);

export const fetchUserById = createAsyncThunk(
  'admin/fetchUserById',
  async (id, { rejectWithValue }) => {
    try {
      const response = await adminService.getUserById(id);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchUserBookings = createAsyncThunk(
  'admin/fetchUserBookings',
  async ({ id, params }, { rejectWithValue }) => {
    try {
      const response = await adminService.getUserBookings(id, params);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const updateUserStatus = createAsyncThunk(
  'admin/updateUserStatus',
  async ({ id, status }, { rejectWithValue }) => {
    try {
      const response = await adminService.updateUserStatus(id, status);
      return { id, status, ...response.data };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const updateUser = createAsyncThunk(
  'admin/updateUser',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await adminService.updateUser(id, data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const deleteUser = createAsyncThunk(
  'admin/deleteUser',
  async (id, { rejectWithValue }) => {
    try {
      const response = await adminService.deleteUser(id);
      return { id, ...response.data };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const restoreUser = createAsyncThunk(
  'admin/restoreUser',
  async (id, { rejectWithValue }) => {
    try {
      const response = await adminService.restoreUser(id);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const permanentDeleteUser = createAsyncThunk(
  'admin/permanentDeleteUser',
  async (id, { rejectWithValue }) => {
    try {
      const response = await adminService.permanentDeleteUser(id);
      return { id, ...response.data };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const changeUserPassword = createAsyncThunk(
  'admin/changeUserPassword',
  async ({ id, password }, { rejectWithValue }) => {
    try {
      const response = await adminService.changeUserPassword(id, password);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const sendMessageToUser = createAsyncThunk(
  'admin/sendMessageToUser',
  async ({ id, message, messageType, emailTitle }, { rejectWithValue }) => {
    try {
      const response = await adminService.sendMessageToUser(id, message, messageType, emailTitle);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const exportUsersExcel = createAsyncThunk(
  'admin/exportUsersExcel',
  async (_, { rejectWithValue }) => {
    try {
      const response = await adminService.exportUsersExcel();
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const exportUsersPDF = createAsyncThunk(
  'admin/exportUsersPDF',
  async (_, { rejectWithValue }) => {
    try {
      const response = await adminService.exportUsersPDF();
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const forceLogoutUser = createAsyncThunk(
  'admin/forceLogoutUser',
  async (id, { rejectWithValue }) => {
    try {
      const response = await adminService.forceLogoutUser(id);
      return { id, ...response.data };
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to force logout user');
    }
  }
);

export const fetchAirlinersAircraft = createAsyncThunk(
  'admin/fetchAirlinersAircraft',
  async (params, { rejectWithValue }) => {
    try {
      const response = await adminService.getAirlinersAircraft(params);
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const addAirliner = createAsyncThunk(
  'admin/addAirliner',
  async (data, { rejectWithValue }) => {
    try {
      const response = await adminService.addAirliner(data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const addJetSetter = createAsyncThunk(
  'admin/addJetSetter',
  async (data, { rejectWithValue }) => {
    try {
      const response = await adminService.addJetSetter(data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const addSubAdmin = createAsyncThunk(
  'admin/addSubAdmin',
  async (data, { rejectWithValue }) => {
    try {
      const response = await adminService.addSubAdmin(data);
      // Return full response to ensure top-level fields like success/data.id are preserved
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// SAX Permission Thunks

/**
 * Fetch the logged-in user's own permission IDs.
 * Used by AdminLayout sidebar and route guards.
 */
export const fetchMyPermissions = createAsyncThunk(
  'admin/fetchMyPermissions',
  async (_, { rejectWithValue }) => {
    try {
      const response = await adminService.getMyPermissions();
      return response; // { success, is_admin, permission_ids }
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchModulesPermissions = createAsyncThunk(
  'admin/fetchModulesPermissions',
  async (_, { rejectWithValue }) => {
    try {
      const response = await adminService.getModulesPermissions();
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchUserPermissions = createAsyncThunk(
  'admin/fetchUserPermissions',
  async (id, { rejectWithValue }) => {
    try {
      const response = await adminService.getUserPermissions(id);
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const updateUserPermissions = createAsyncThunk(
  'admin/updateUserPermissions',
  async ({ id, permissions }, { rejectWithValue }) => {
    try {
      const response = await adminService.updateUserPermissions(id, permissions);
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchBookingLifecycle = createAsyncThunk(
  'admin/fetchBookingLifecycle',
  async (token, { rejectWithValue }) => {
    try {
      const response = await adminService.getBookingLifecycle(token);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Initial state
const initialState = {
  users: [],
  selectedUser: null,
  userBookings: [],
  bookingsPagination: {
    total: 0,
    page: 1,
    limit: 10,
    total_pages: 0
  },
  pagination: {
    total: 0,
    page: 1,
    limit: 10,
    total_pages: 0
  },
  loading: false,
  bookingsLoading: false,
  error: null,
  statusUpdateLoading: false,
  deleteLoading: false,
  passwordChangeLoading: false,
  messageLoading: false,
  addUserLoading: false,
  
  // SAX Permissions
  modulesPermissions: [],
  userPermissions: null,
  permissionsLoading: false,
  updatingPermissions: false,

  // Logged-in user's own permissions (for sidebar/route guards)
  myPermissions: null, // { is_admin: bool, permission_ids: number[] }
  
  // Booking Lifecycle
  bookingLifecycle: null,
  lifecycleLoading: false,
  lifecycleError: null,

  // Force Logout
  forceLogoutLoading: false
};

// Slice
const adminSlice = createSlice({
  name: 'admin',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearSelectedUser: (state) => {
      state.selectedUser = null;
    },
    clearUserBookings: (state) => {
      state.userBookings = [];
      state.bookingsPagination = {
        total: 0,
        page: 1,
        limit: 10,
        total_pages: 0
      };
    },
    setPagination: (state, action) => {
      state.pagination = { ...state.pagination, ...action.payload };
    },
    clearUserPermissions: (state) => {
      state.userPermissions = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch Users
      .addCase(fetchUsers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUsers.fulfilled, (state, action) => {
        state.loading = false;
        state.users = action.payload.data.users;
        state.pagination = action.payload.data.pagination;
      })
      .addCase(fetchUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Fetch User By ID
      .addCase(fetchUserById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUserById.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedUser = action.payload;
      })
      .addCase(fetchUserById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Fetch User Bookings
      .addCase(fetchUserBookings.pending, (state) => {
        state.bookingsLoading = true;
        state.error = null;
      })
      .addCase(fetchUserBookings.fulfilled, (state, action) => {
        state.bookingsLoading = false;
        state.userBookings = action.payload.bookings;
        state.bookingsPagination = action.payload.pagination;
      })
      .addCase(fetchUserBookings.rejected, (state, action) => {
        state.bookingsLoading = false;
        state.error = action.payload;
      })

      // Update User Status
      .addCase(updateUserStatus.pending, (state) => {
        state.statusUpdateLoading = true;
        state.error = null;
      })
      .addCase(updateUserStatus.fulfilled, (state, action) => {
        state.statusUpdateLoading = false;
        // Update user in the list
        const index = state.users.findIndex(u => u.id === action.payload.id);
        if (index !== -1) {
          state.users[index].status = action.payload.status;
        }
      })
      .addCase(updateUserStatus.rejected, (state, action) => {
        state.statusUpdateLoading = false;
        state.error = action.payload;
      })

      // Update User
      .addCase(updateUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateUser.fulfilled, (state, action) => {
        state.loading = false;
        // Update user in the list
        const index = state.users.findIndex(u => u.id === action.payload.id);
        if (index !== -1) {
          state.users[index] = action.payload;
        }
        if (state.selectedUser?.id === action.payload.id) {
          state.selectedUser = action.payload;
        }
      })
      .addCase(updateUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // SAX Permissions Builders
      .addCase(fetchMyPermissions.pending, (state) => {
        state.permissionsLoading = true;
      })
      .addCase(fetchMyPermissions.fulfilled, (state, action) => {
        state.permissionsLoading = false;
        state.myPermissions = {
          is_admin: action.payload.is_admin || false,
          permission_ids: action.payload.permission_ids || []
        };
      })
      .addCase(fetchMyPermissions.rejected, (state) => {
        state.permissionsLoading = false;
        // On failure, default to no permissions (safe fallback)
        state.myPermissions = { is_admin: false, permission_ids: [] };
      })
      .addCase(fetchModulesPermissions.pending, (state) => {
        state.permissionsLoading = true;
      })
      .addCase(fetchModulesPermissions.fulfilled, (state, action) => {
        state.permissionsLoading = false;
        state.modulesPermissions = action.payload.data || [];
      })
      .addCase(fetchModulesPermissions.rejected, (state, action) => {
        state.permissionsLoading = false;
        state.error = action.payload;
      })
      .addCase(fetchUserPermissions.pending, (state) => {
        state.permissionsLoading = true;
      })
      .addCase(fetchUserPermissions.fulfilled, (state, action) => {
        state.permissionsLoading = false;
        state.userPermissions = action.payload;
      })
      .addCase(fetchUserPermissions.rejected, (state, action) => {
        state.permissionsLoading = false;
        state.error = action.payload;
      })
      .addCase(updateUserPermissions.pending, (state) => {
        state.updatingPermissions = true;
      })
      .addCase(updateUserPermissions.fulfilled, (state) => {
        state.updatingPermissions = false;
      })
      .addCase(updateUserPermissions.rejected, (state, action) => {
        state.updatingPermissions = false;
        state.error = action.payload;
      })

      // Delete User
      .addCase(deleteUser.pending, (state) => {
        state.deleteLoading = true;
        state.error = null;
      })
      .addCase(deleteUser.fulfilled, (state, action) => {
        state.deleteLoading = false;
        // Remove user from the list
        state.users = state.users.filter(u => u.id !== action.payload.id);
        state.pagination.total -= 1;
      })
      .addCase(deleteUser.rejected, (state, action) => {
        state.deleteLoading = false;
        state.error = action.payload;
      })

      // Restore User
      .addCase(restoreUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(restoreUser.fulfilled, (state, action) => {
        state.loading = false;
        // Add restored user back to the list
        state.users.push(action.payload);
        state.pagination.total += 1;
      })
      .addCase(restoreUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Permanent Delete User
      .addCase(permanentDeleteUser.pending, (state) => {
        state.deleteLoading = true;
        state.error = null;
      })
      .addCase(permanentDeleteUser.fulfilled, (state, action) => {
        state.deleteLoading = false;
        // Remove user from the list
        state.users = state.users.filter(u => u.id !== action.payload.id);
        state.pagination.total -= 1;
      })
      .addCase(permanentDeleteUser.rejected, (state, action) => {
        state.deleteLoading = false;
        state.error = action.payload;
      })

      // Change User Password
      .addCase(changeUserPassword.pending, (state) => {
        state.passwordChangeLoading = true;
        state.error = null;
      })
      .addCase(changeUserPassword.fulfilled, (state) => {
        state.passwordChangeLoading = false;
      })
      .addCase(changeUserPassword.rejected, (state, action) => {
        state.passwordChangeLoading = false;
        state.error = action.payload;
      })

      // Send Message To User
      .addCase(sendMessageToUser.pending, (state) => {
        state.messageLoading = true;
        state.error = null;
      })
      .addCase(sendMessageToUser.fulfilled, (state) => {
        state.messageLoading = false;
      })
      .addCase(sendMessageToUser.rejected, (state, action) => {
        state.messageLoading = false;
        state.error = action.payload;
      })

      // Force Logout User
      .addCase(forceLogoutUser.pending, (state) => {
        state.forceLogoutLoading = true;
        state.error = null;
      })
      .addCase(forceLogoutUser.fulfilled, (state) => {
        state.forceLogoutLoading = false;
      })
      .addCase(forceLogoutUser.rejected, (state, action) => {
        state.forceLogoutLoading = false;
        state.error = action.payload;
      })

      // Fetch Booking Lifecycle
      .addCase(fetchBookingLifecycle.pending, (state) => {
        state.lifecycleLoading = true;
        state.lifecycleError = null;
      })
      .addCase(fetchBookingLifecycle.fulfilled, (state, action) => {
        state.lifecycleLoading = false;
        state.bookingLifecycle = action.payload;
      })
      .addCase(fetchBookingLifecycle.rejected, (state, action) => {
        state.lifecycleLoading = false;
        state.lifecycleError = action.payload;
      })

      // Add User Matchers
      .addMatcher(
        (action) => action.type.endsWith('/pending') && (
          action.type.includes('addAirliner') ||
          action.type.includes('addJetSetter') ||
          action.type.includes('addSubAdmin')
        ),
        (state) => {
          state.addUserLoading = true;
          state.error = null;
        }
      )
      .addMatcher(
        (action) => action.type.endsWith('/fulfilled') && (
          action.type.includes('addAirliner') ||
          action.type.includes('addJetSetter') ||
          action.type.includes('addSubAdmin')
        ),
        (state) => {
          state.addUserLoading = false;
        }
      )
      .addMatcher(
        (action) => action.type.endsWith('/rejected') && (
          action.type.includes('addAirliner') ||
          action.type.includes('addJetSetter') ||
          action.type.includes('addSubAdmin')
        ),
        (state, action) => {
          state.addUserLoading = false;
          state.error = action.payload;
        }
      );
  }
});

export const { clearError, clearSelectedUser, clearUserBookings, setPagination, clearUserPermissions } = adminSlice.actions;
export default adminSlice.reducer;
